import clsx from "clsx";
import React, { useCallback, useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardProps,
  Select,
  SelectProps,
  Typography,
  Switch,
  capitalize,
  Tooltip,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Skeleton,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import {
  BillingPlan,
  billingPlanPrice,
  LAPSED_COACH_LOCAL_STORAGE_KEY,
  monthlyPlansInOrder,
  Plan,
  planClients,
  planOrder,
  planStatus,
  PlanTier,
  SOMETHING_WENT_WRONG,
  tierIndex,
  yearlyPlansInOrder,
  ADDON_CATALOG,
  AddonName,
  AddonCatalogItem,
  TeamRole,
} from "../../constants";

import { useQueryParam } from "../../hooks/useQueryParam";
import { initStripe, stripeAction } from "../../stripe";
import { LinkButton } from "../button/LinkButton";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";
import { CBAPreactivationDialog } from "../dialog/CBAPreactivationDialog";
import { useNavigate } from "react-router-dom";
import { UserInfo } from "../../hooks/useCurrentUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import BillingsService from "../../services/BillingsService";
import { COACH_BRANDED_APP_ROUTE, COACH_PLAN_ROUTE } from "../../routes/routes";
import { CURRENT_USER_QUERY_KEY } from "../../wrappers/current-user/CurrentUserWrapper";
import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";
import {
  getNextHigherPlan,
  getPlanAction,
  isPlanYearly,
  monthlyBillingPlan,
  getAddonPriceDisplayText,
  getAddonUnitPrice,
  getAddonTotalPrice,
} from "../../utils/user";

import { SelectClientsDialog } from "../dialog/SelectClientsDialog";
import ClientsService from "../../services/ClientsService";
import useArchiveClientsMutation from "../coach-clients/mutations/useArchiveClientsMutation";
import {
  ClientInfoDto,
  CustomerPortalFlowType,
  UserInviteStatus,
  AddonCode,
  BillingInterval,
  AddonUpdateDto,
  AddonCatalogItemDto,
  AddonDto,
} from "@growth-machine-llc/stridist-api-client";
import { SubscriptionUpdatePolling } from "./SubscriptionUpdatePolling";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useMediaMobile } from "../../hooks/useMediaMobile";
import FbUsersService from "../../services/FbUsersService";
import { InvoiceBreakdownDialog } from "../dialog/InvoiceBreakdownDialog";
import {
  ADDONS_QUERY_KEY,
  useAddons,
  useAddonsCatalog,
} from "../../hooks/useAddons";
import { colorSystem } from "../../theme";
import AnimateHeightBox from "../new-editor/elements/common/AnimateHeightBox";
import { InfoOutlined } from "@mui/icons-material";
import { formatCurrency } from "../../utils/currency";
import { useQuery } from "@tanstack/react-query";
import TeamService from "../../services/TeamService";
import {
  TEAM_COACHES_QUERY_KEY,
  TEAM_INVITED_COACHES_QUERY_KEY,
} from "../../routes/coach/team/CoachTeamRoute";
import { COACH_TEAM_ACTIVE_ROUTE } from "../../routes/routes";
import { TeamCoachStatus } from "@growth-machine-llc/stridist-api-client";
import { Link } from "@mui/material";
import { useGenericErrorHandler } from "../../hooks/useGenericErrorHandler";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(5, 4),
    width: 558,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
    boxShadow: "none",
    border: `1px solid ${colorSystem.gray5}`,
    borderRadius: theme.shape.radius.md,

    [theme.breakpoints.down("md")]: {
      width: "100%",
      padding: theme.spacing(4, 3),
    },
  },

  title: {
    fontSize: 26,
    fontWeight: 700,
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1.5),
  },

  subtitle: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },

  addonsTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1),
  },

  planSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },

  planLabel: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1),
  },

  helperText: {
    fontSize: 13,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    lineHeight: 1.4,
  },

  planImage: {
    width: "100%",
    height: 24,
    objectFit: "cover",
    borderRadius: theme.spacing(0.5),
  },

  planDropdown: {
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.spacing(1),
      borderColor: "#C4C4C4",
      padding: theme.spacing(1.5, 2),
      minHeight: 48,
      "&:hover": {
        borderColor: "#C4C4C4",
      },
      "&.Mui-focused": {
        borderColor: "#C4C4C4",
      },
    },
    "& .MuiSelect-select": {
      fontSize: 16,
      fontWeight: 500,
      padding: 0,
      display: "flex",
      alignItems: "center",
    },
    "& .MuiSelect-icon": {
      color: theme.palette.text.secondary,
      fontSize: 28,
    },
  },

  addonsSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  addonSwitch: {
    "& .MuiSwitch-track": {
      backgroundColor: theme.palette.grey[300],
    },
  },

  addonCard: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: "#F3F5FA",
    borderRadius: theme.spacing(1.5),
    gap: theme.spacing(1.5),
  },

  addonInfo: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    flex: 1,
  },

  addonHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },

  addonInfoIcon: {
    fontSize: 24,
    color: theme.palette.primary.main,
    cursor: "pointer",
  },

  addonName: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.common.black,
  },

  addonDescription: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },

  addonControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2.625),
    marginLeft: "auto",
  },

  quantityControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2.625),
  },

  quantityButton: {
    width: 32,
    height: 32,
    minWidth: 32,
    padding: 0,
    borderRadius: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.common.white,
    "&:disabled": {
      opacity: 0.5,
    },
  },

  quantityValue: {
    fontSize: 16,
    fontWeight: 400,
    color: theme.palette.common.black,
    minWidth: 20,
    textAlign: "center",
  },

  summarySection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.25),
  },

  summaryTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: theme.palette.common.black,
  },

  summaryItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },

  summaryItemLeft: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    flex: 1,
  },

  summaryItemName: {
    fontSize: 16,
    fontWeight: 400,
    color: theme.palette.common.black,
  },

  summaryItemDescription: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    marginTop: theme.spacing(2),
  },

  summarySubtotal: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(2),
    borderTop: `1px solid ${colorSystem.gray5}`,
  },

  summarySubtotalLabel: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.common.black,
  },

  summarySubtotalPrice: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.common.black,
  },

  summaryHint: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    marginTop: theme.spacing(1),
    fontStyle: "italic",
  },

  addonHint: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    fontStyle: "italic",
    textAlign: "end",
  },
  addonHintInSummary: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    textAlign: "left",
    marginTop: theme.spacing(1),
  },

  summaryItemPrice: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.common.black,
  },

  summaryPricePeriod: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
  },

  button: {
    height: 48,
    padding: theme.spacing(1.5, 2.5),
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.spacing(0.75),
    textTransform: "none",
    transition: theme.transitions.create("opacity", {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut,
    }),

    "&:hover": {
      backgroundColor: theme.palette.primary.main,
    },

    "&.Mui-disabled": {
      color: theme.palette.common.white,
      opacity: 0.5,
    },
  },

  billingCycleLabel: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1.5),
  },

  billingCycleRadioGroup: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(3),
  },

  billingCycleOption: {
    margin: 0,
    alignItems: "center",
    "& .MuiRadio-root": {
      padding: theme.spacing(0.5),
      color: theme.palette.text.secondary,
      "&.Mui-checked": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiFormControlLabel-label": {
      fontSize: 15,
      fontWeight: 500,
      color: theme.palette.common.black,
      padding: 0,
      position: "relative",
      marginLeft: theme.spacing(0.75),
    },
  },

  billingCycleOptionChecked: {
    "& .MuiFormControlLabel-label": {
      fontWeight: 600,
    },
  },

  billingCycleSavingsBadge: {
    position: "absolute",
    top: 4,
    left: "100%",
    marginLeft: theme.spacing(1.5),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(0.5),
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    boxShadow: `0 2px 4px rgba(0, 0, 0, 0.1)`,
    zIndex: 1,
  },

  billingCycleDescription: {
    fontSize: 13,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },

  highlightedText: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 1.5, 0.5, 0.8),
    borderRadius: theme.shape.radius.md,
    marginLeft: theme.spacing(1),
    fontSize: 12,
    fontWeight: "bold",
    lineHeight: "1.5",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  icon: {
    fontSize: theme.typography.fontSize * 1.5,
  },

  planUpgradeInfo: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    marginTop: theme.spacing(0.5),
  },

  titleModal: {
    lineBreak: "anywhere",
  },

  preactivationDialog: {
    "& .MuiDialog-paper": {
      maxWidth: 600,
    },
  },

  preactivationDescription: {
    fontSize: 16,
    fontWeight: 400,
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    margin: 0,
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
      fontWeight: 500,
      "&:hover": {
        textDecoration: "underline",
      },
    },
    "& strong": {
      fontWeight: 600,
      color: theme.palette.common.black,
    },
  },
}));

export interface ProUpgradeCardProps extends CardProps {
  user: UserInfo;
  onPlanChange?: (plan: Plan) => void;
}

enum UpgradeStatus {
  SUCCESS = "success",
  ERROR = "error",
}

export const LAPSED_COACH_ACTIVATION_MUTATION_KEY = "lapsed-coach-activation";

export function ProUpgradeCard(props: ProUpgradeCardProps) {
  const { className, user, onPlanChange, ...other } = props;
  const navigate = useNavigate();
  const s = useStyles();
  const isMobile = useMediaMobile();
  const queryClient = useQueryClient();
  const [newPlan] = useQueryParam("plan", "");
  const [downgraded] = useQueryParam("downgraded", "");
  const [prevStatus] = useQueryParam("prevStatus", "");
  const [planUpdateStatus] = useQueryParam<UpgradeStatus>("upgrade");
  const { showToastAlert } = useToastAlert();
  const { data: currentAddons, isLoading: isLoadingCurrentAddons } =
    useAddons();
  const { data: addonsCatalog, isLoading: isLoadingAddonsCatalog } =
    useAddonsCatalog();
  const handleGenericError = useGenericErrorHandler({
    disableBadRequestSnackbar: false,
  });

  // Fetch active coaches (excluding archived) and invited coaches to calculate minimum seats
  const { data: activeCoaches, isLoading: isLoadingActiveCoaches } = useQuery({
    queryKey: [TEAM_COACHES_QUERY_KEY, TeamCoachStatus.Active],
    queryFn: () => TeamService.getCoaches(TeamCoachStatus.Active),
  });

  const { data: invitedCoaches, isLoading: isLoadingInvitedCoaches } = useQuery(
    {
      queryKey: [TEAM_INVITED_COACHES_QUERY_KEY],
      queryFn: () => TeamService.getInvitedCoaches(),
    },
  );

  const isAddonsLoading = isLoadingCurrentAddons || isLoadingAddonsCatalog;
  const isCoachCountsLoading =
    isLoadingActiveCoaches || isLoadingInvitedCoaches;

  const minimumSeatsCount = useMemo(() => {
    const activeCount =
      activeCoaches?.filter((coach) => coach.teamRole === TeamRole.COACH)
        .length ?? 0;
    const invitedCount = invitedCoaches?.length ?? 0;
    return activeCount + invitedCount;
  }, [activeCoaches, invitedCoaches]);

  const [
    downgradeSelectClientsDialogOpen,
    setDowngradeSelectClientsDialogOpen,
  ] = useState(false);

  const nextHigherPlan = React.useMemo(() => {
    const userPlan = user.plan as Plan;
    return getNextHigherPlan(userPlan);
  }, [user.plan]);

  const defaultPlan = React.useMemo(() => {
    if (
      user?.subscription?.status === planStatus.CANCELED ||
      user?.subscription?.status === planStatus.INCOMPLETE_EXPIRED
    ) {
      return user.plan;
    }
    return newPlan ? (newPlan as Plan) : user.plan;
  }, [user, newPlan, nextHigherPlan]);

  const [selectedPlan, setSelectedPlan] = React.useState<Plan>(
    defaultPlan as Plan,
  );

  const [billingCycle, setBillingCycle] = React.useState<"monthly" | "annual">(
    user.plan.includes("YEARLY") ? "annual" : "monthly",
  );

  // Addon state management
  const billingIntervalKey =
    billingCycle === "annual" ? BillingInterval.YEAR : BillingInterval.MONTH;

  // Get available addons from catalog API
  const billingIntervalAddons = React.useMemo(() => {
    if (!addonsCatalog || !addonsCatalog[billingIntervalKey]) {
      return [];
    }
    return (addonsCatalog[billingIntervalKey] || []).map((addon) => ({
      billingInterval: billingIntervalKey,
      ...addon,
      ...(ADDON_CATALOG[addon.code] || {}),
    }));
  }, [addonsCatalog, billingIntervalKey]);

  const [addonQuantities, setAddonQuantities] = useState<
    Record<string, number>
  >(() => {
    const initial: Record<string, number> = {};
    billingIntervalAddons.forEach((addon) => {
      const existingAddon = currentAddons?.find(
        (a) => a.code.toString() === addon.code,
      );
      initial[addon.code] = existingAddon?.quantity ?? 0;
    });
    return initial;
  });

  // Initialize quantities for addons in current billing interval
  React.useEffect(() => {
    if (currentAddons && billingIntervalAddons.length > 0) {
      setAddonQuantities((prevQuantities) => {
        const updated: Record<string, number> = {};
        billingIntervalAddons.forEach((addon) => {
          const existingAddon = currentAddons.find(
            (a) => a.code.toString() === addon.code,
          );
          const isUnitBased = addon.isUnitBased ?? false;
          const existingQuantity = existingAddon?.quantity ?? 0;

          // Preserve user's changes if they exist, otherwise use server value
          const prevQuantity = prevQuantities[addon.code];
          if (prevQuantity !== undefined) {
            updated[addon.code] = isUnitBased
              ? Math.max(0, prevQuantity)
              : Math.min(Math.max(0, prevQuantity), 1);
          } else {
            // Use server value for initialization
            updated[addon.code] = isUnitBased
              ? existingQuantity
              : Math.min(existingQuantity, 1);
          }
        });
        return updated;
      });
    }
  }, [currentAddons, billingIntervalAddons]);

  const handleBillingCycleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!user.plan.includes("YEARLY")) {
      const newBillingCycle = event.target.value as "monthly" | "annual";
      setBillingCycle(newBillingCycle);

      let newPlan;
      if (selectedPlan === Plan.UP_TO_UNLIMITED) {
        newPlan =
          newBillingCycle === "annual"
            ? Plan.YEARLY_UNLIMITED
            : Plan.UP_TO_UNLIMITED;
      } else if (selectedPlan === Plan.YEARLY_UNLIMITED) {
        newPlan =
          newBillingCycle === "annual"
            ? Plan.YEARLY_UNLIMITED
            : Plan.UP_TO_UNLIMITED;
      } else {
        newPlan =
          newBillingCycle === "annual"
            ? selectedPlan.replace("UP_TO_", "YEARLY_UP_TO_")
            : selectedPlan.replace("YEARLY_UP_TO_", "UP_TO_");
      }
      setSelectedPlan(newPlan as Plan);
    }
  };

  const applyAddonQuantityChange = React.useCallback(
    (addonCode: string, newQuantity: number) => {
      setAddonQuantities((prev) => ({
        ...prev,
        [addonCode]: newQuantity,
      }));
      queryClient.invalidateQueries({ queryKey: [CURRENT_USER_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ADDONS_QUERY_KEY] });
    },
    [],
  );

  const handleAddonQuantityChange = (addonCode: string, delta: number) => {
    const current = addonQuantities[addonCode] || 0;
    const addon = billingIntervalAddons.find((a) => a.code === addonCode);
    if (!addon || !canEditAddon(addonCode as AddonCode)) {
      return;
    }
    const isUnitBased = addon?.isUnitBased ?? false;

    let newQuantity: number;

    // For non-unit-based addons (like CBA), limit to max 1
    if (!isUnitBased) {
      newQuantity = current === 0 ? 1 : 0;
    } else {
      // For unit-based addons, allow any quantity >= 0
      newQuantity = Math.max(0, current + delta);
    }

    // TODO_ADDONS: Handle this generically for all addons
    if (
      addonCode === AddonCode.TEAM_ACCOUNT &&
      delta < 0 &&
      newQuantity < minimumSeatsCount
    ) {
      showToastAlert("error", {
        message: `Cannot decrease team seats below ${minimumSeatsCount} (${minimumSeatsCount - (invitedCoaches?.length ?? 0)} active + ${invitedCoaches?.length ?? 0} invited). Please archive coaches or revoke invites first.`,
      });
      return;
    }

    // Check if we're activating for the first time (going from 0 to >0)
    // and if there's a preactivation message
    if (current === 0 && newQuantity > 0 && addon?.preactivationTitle) {
      // Show pre-activation dialog
      setPendingAddonActivation({ addonCode, newQuantity });
      setPreactivationDialogOpen(true);
    } else {
      // Directly apply the change
      applyAddonQuantityChange(addonCode, newQuantity);
    }
  };

  const handleAddonToggle = (addonCode: string) => {
    const current = addonQuantities[addonCode] || 0;
    const addon = billingIntervalAddons.find((a) => a.code === addonCode);
    if (!addon || !canEditAddon(addonCode as AddonCode)) {
      return;
    }
    const newQuantity = current > 0 ? 0 : 1;

    // Check if we're activating for the first time (going from 0 to 1)
    // and if there's a preactivation message
    if (current === 0 && newQuantity > 0 && addon?.preactivationTitle) {
      // Show pre-activation dialog
      setPendingAddonActivation({ addonCode, newQuantity });
      setPreactivationDialogOpen(true);
    } else {
      // Directly apply the change
      applyAddonQuantityChange(addonCode, newQuantity);
    }
  };

  const [showUpsellYearlyPlan, setShowUpsellYearlyPlan] = React.useState(false);
  const [upsellYearlyFee, setUpsellYearlyFee] = React.useState(0);
  const [showInvoiceModal, setShowInvoiceModal] = React.useState(false);

  // Pre-activation dialog state
  const [preactivationDialogOpen, setPreactivationDialogOpen] =
    React.useState(false);
  const [pendingAddonActivation, setPendingAddonActivation] = React.useState<{
    addonCode: string;
    newQuantity: number;
  } | null>(null);

  const handlePreactivationConfirm = React.useCallback(() => {
    if (pendingAddonActivation) {
      applyAddonQuantityChange(
        pendingAddonActivation.addonCode,
        pendingAddonActivation.newQuantity,
      );
    }
    setPreactivationDialogOpen(false);
    setPendingAddonActivation(null);
  }, [pendingAddonActivation, applyAddonQuantityChange]);

  const handlePreactivationCancel = React.useCallback(() => {
    setPreactivationDialogOpen(false);
    setPendingAddonActivation(null);
  }, []);

  React.useEffect(() => {
    // should we show this modal?
    const shouldShow =
      window.location.href.toLowerCase().includes("promoyearly") &&
      user?.plan.includes("YEARLY") === false &&
      user?.planTier == PlanTier.DEFAULT;
    if (shouldShow) {
      setShowUpsellYearlyPlan(shouldShow);
      // get anual cost
      // Map the selected plan to the corresponding plan in the other list
      let newPlan;
      if (selectedPlan === Plan.UP_TO_UNLIMITED) {
        newPlan = Plan.YEARLY_UNLIMITED;
      } else {
        newPlan = selectedPlan.replace("UP_TO_", "YEARLY_UP_TO_");
      }

      setUpsellYearlyFee(
        billingPlanPrice[newPlan][tierIndex[PlanTier.DEFAULT]],
      );
      setSelectedPlan(newPlan as Plan);
    }
  }, []);

  const cancelYearlyUpsell = () => {
    setSelectedPlan(user?.plan as Plan);
    setShowUpsellYearlyPlan(false);
  };

  const isDowngrade = React.useMemo(() => {
    if (
      user?.subscription?.status === planStatus.CANCELED ||
      user?.subscription?.status === planStatus.INCOMPLETE_EXPIRED
    )
      return false;

    const storedCurrentPlan = localStorage.getItem("currentPlan");
    const storedSelectedPlan = localStorage.getItem("selectedPlan");

    const currentPlanEffective = storedCurrentPlan || user.plan;
    const selectedPlanEffective = storedSelectedPlan || selectedPlan;

    const currentPlanIndex = planOrder.indexOf(currentPlanEffective as Plan);
    const selectedPlanIndex = planOrder.indexOf(selectedPlanEffective as Plan);

    return selectedPlanIndex < currentPlanIndex;
  }, [user.plan, selectedPlan]);

  const [openPolling, setOpenPolling] = useState(false);

  const {
    mutate: createCheckoutSession,
    isPending: creatingCheckoutSession,
    isSuccess: createdCheckoutSession,
  } = useMutation({
    mutationKey: ["create-checkout-session"],
    mutationFn: BillingsService.createCheckoutSession,
  });

  const { mutate: lapsedCoachActivation } = useMutation({
    mutationKey: [LAPSED_COACH_ACTIVATION_MUTATION_KEY],
    mutationFn: FbUsersService.lapsedCoachActivation,
  });

  const handleClosePolling = useCallback(() => {
    setOpenPolling(false);
    localStorage.removeItem("pollingOpen");
    localStorage.removeItem("selectedPlan");
    localStorage.removeItem("currentPlan");

    // Invalidate queries to refresh data
    queryClient.invalidateQueries({ queryKey: [CURRENT_USER_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [ADDONS_QUERY_KEY] });

    const isUpgrade = prevStatus || !isDowngrade;

    const isLapsedCoach =
      localStorage.getItem(LAPSED_COACH_LOCAL_STORAGE_KEY) === "true";

    if (isLapsedCoach) {
      lapsedCoachActivation({
        coachId: user.id,
      });
    }

    localStorage.removeItem(LAPSED_COACH_LOCAL_STORAGE_KEY);

    if (isUpgrade) {
      const choosePlan =
        user?.subscription?.status === planStatus.TRIALING
          ? ""
          : `&plan=${selectedPlan}`;
      const path = COACH_PLAN_ROUTE + "?upgraded=true" + choosePlan;

      navigate(path, { replace: true });
    } else {
      showToastAlert("success", {
        message: "Plan downgraded.",
      });
      navigate(COACH_PLAN_ROUTE, { replace: true });
    }
  }, [
    navigate,
    selectedPlan,
    showToastAlert,
    user?.subscription?.status,
    queryClient,
    user.id,
    prevStatus,
    isDowngrade,
    lapsedCoachActivation,
  ]);

  React.useEffect(() => {
    if (openPolling) {
      localStorage.setItem("pollingOpen", "true");
      localStorage.setItem("selectedPlan", selectedPlan);
      localStorage.setItem("currentPlan", user.plan);
    }
  }, [openPolling, selectedPlan, user.plan]);

  React.useEffect(() => {
    const storedPollingOpen = localStorage.getItem("pollingOpen");
    const storedSelectedPlan = localStorage.getItem("selectedPlan");
    if (storedPollingOpen === "true" && storedSelectedPlan) {
      setOpenPolling(true);
      setSelectedPlan(storedSelectedPlan as Plan);
    }
  }, []);

  React.useEffect(() => {
    if (
      user?.subscription?.status === planStatus.PAST_DUE ||
      user?.subscription?.status === planStatus.INCOMPLETE ||
      user?.subscription?.status === planStatus.UNPAID
    ) {
      setSelectedPlan(user.plan as Plan);
    }
  }, [user?.subscription?.status, user.plan]);

  const { mutate: batchArchiveClients, isPending: batchingArchiveClients } =
    useArchiveClientsMutation([UserInviteStatus.ACTIVE]);

  const { mutate: getHostedInvoiceUrl, isPending: getHostedInvoiceUrlIn } =
    useMutation({
      mutationKey: ["get-hosted-invoice-url"],
      mutationFn: BillingsService.getHostedInvoiceUrl,
    });

  const {
    mutate: updateSubscriptionWithAddons,
    isPending: updatingSubscriptionWithAddons,
  } = useMutation({
    mutationKey: ["update-subscription-with-addons"],
    mutationFn: BillingsService.updateSubscriptionWithAddons,
    onError: handleGenericError,
  });

  const hasAddonChanges = React.useMemo(() => {
    return billingIntervalAddons.some((addon) => {
      const currentAddon = currentAddons?.find((a) => a.code === addon.code);
      const currentQuantity = currentAddon?.quantity ?? 0;
      const newQuantity = addonQuantities[addon.code] || 0;
      return currentQuantity !== newQuantity;
    });
  }, [billingIntervalAddons, currentAddons, addonQuantities]);

  const getSubscriptionUpdatePayload = React.useCallback(() => {
    // NOTE: Includes only items that have changed due to request method is PATCH
    const planChanged = selectedPlan !== user.plan;
    const plan = planChanged ? selectedPlan : undefined;

    const addons = billingIntervalAddons.reduce((acc, addon) => {
      const currentAddon = currentAddons?.find((a) => a.code === addon.code);
      const currentQuantity = currentAddon?.quantity ?? 0;
      const currentBillingInterval = currentAddon?.billingInterval;
      const isCurrentAddonActive = currentQuantity > 0;
      const newBillingInterval = addon.billingInterval;
      const newQuantity = addonQuantities[addon.code] || 0;

      const isQuantityChanged = currentQuantity !== newQuantity;
      const isBillingIntervalChanged =
        currentBillingInterval !== newBillingInterval;

      if (
        (isBillingIntervalChanged && isCurrentAddonActive) ||
        isQuantityChanged
      ) {
        acc.push(
          AddonUpdateDto.fromJS({
            addonCode: addon.code as unknown as AddonCode,
            quantity: newQuantity,
          }),
        );
      }
      return acc;
    }, [] as Array<AddonUpdateDto>);

    return { plan, addons };
  }, [
    selectedPlan,
    user.plan,
    billingIntervalAddons,
    currentAddons,
    addonQuantities,
  ]);

  const isCanceled = user?.subscription?.status === planStatus.CANCELED;
  const successUrlParam = isDowngrade
    ? "downgraded=true"
    : `upgrade=${UpgradeStatus.SUCCESS}${isCanceled ? `&prevStatus=${user?.subscription?.status}` : ""}`;

  React.useEffect(() => {
    if (
      newPlan &&
      (downgraded === "true" || planUpdateStatus === UpgradeStatus.SUCCESS)
    ) {
      user?.plan === newPlan ? handleClosePolling() : setOpenPolling(true);
    }
  }, [
    downgraded,
    planUpdateStatus,
    newPlan,
    user?.plan,
    user?.subscription?.status,
    navigate,
    showToastAlert,
  ]);

  const returnDataMutation = React.useCallback(
    (status: string) => {
      if (user?.planTier === PlanTier.DEFAULT) {
        return status === planStatus.ACTIVE || status === planStatus.TRIALING;
      } else return status === planStatus.ACTIVE;
    },
    [user],
  );

  const checkStatus = React.useCallback((status: string) => {
    return (
      status === planStatus.INCOMPLETE ||
      status === planStatus.INCOMPLETE_EXPIRED ||
      status === planStatus.PAST_DUE ||
      status === planStatus.UNPAID ||
      status === planStatus.INCOMPLETE ||
      status === planStatus.INCOMPLETE_EXPIRED ||
      status === planStatus.PAST_DUE ||
      status === planStatus.UNPAID
    );
  }, []);

  React.useEffect(() => {
    if (onPlanChange) {
      onPlanChange(selectedPlan);
    }
  }, [onPlanChange, selectedPlan]);

  const checkPlanStatus = useCallback(
    (plan) => {
      return user.clientsCountNoSample > Number(planClients(plan, true));
    },
    [user.clientsCountNoSample],
  );

  const handlePlanChange: SelectProps["onChange"] = React.useCallback(
    (event) => {
      setSelectedPlan(event.target.value as Plan);
    },
    [],
  );

  const handleStripeAction = React.useCallback(
    (id) => {
      stripeAction(id, showToastAlert);
    },
    [showToastAlert],
  );

  const createCheckout = React.useCallback(() => {
    const { addons } = getSubscriptionUpdatePayload();
    createCheckoutSession(
      {
        plan: selectedPlan,
        addons,
        successUrl: `${window.location.href}?${successUrlParam}&plan=${selectedPlan}`,
        cancelUrl: `${window.location.href}?upgrade=${UpgradeStatus.ERROR}`,
      },
      {
        onSuccess: ({ sessionId, isLapsedCoach }) => {
          if (isLapsedCoach) {
            localStorage.setItem(
              LAPSED_COACH_LOCAL_STORAGE_KEY,
              isLapsedCoach.toString(),
            );
          }
          return handleStripeAction(sessionId);
        },
      },
    );
  }, [
    createCheckoutSession,
    showToastAlert,
    selectedPlan,
    stripeAction,
    getSubscriptionUpdatePayload,
  ]);

  const doContinue = React.useCallback(() => {
    if (
      user?.subscription?.status === planStatus.CANCELED ||
      user?.subscription?.status === planStatus.INCOMPLETE_EXPIRED ||
      !user?.subscription?.status
    ) {
      createCheckout();
    } else if (
      user?.subscription?.status === planStatus.INCOMPLETE ||
      user?.subscription?.status === planStatus.PAST_DUE ||
      user?.subscription?.status === planStatus.UNPAID
    ) {
      getHostedInvoiceUrl(undefined, {
        onSuccess: ({ hostedInvoiceUrl }) => {
          window.location.href = `${hostedInvoiceUrl}`;
        },
      });
    } else {
      // Use the new unified endpoint that accepts plan + addons
      const { plan, addons } = getSubscriptionUpdatePayload();

      updateSubscriptionWithAddons(
        {
          planName: plan,
          addons,
        },
        {
          onSuccess: ({ status }) => {
            if (returnDataMutation(status)) {
              setShowInvoiceModal(false);
              setOpenPolling(true);
            }
          },
        },
      );
    }
  }, [
    showToastAlert,
    selectedPlan,
    addonQuantities,
    createCheckout,
    getHostedInvoiceUrl,
    checkStatus,
    returnDataMutation,
    user,
    updateSubscriptionWithAddons,
  ]);

  const openInvoiceModal = React.useCallback(() => {
    setShowInvoiceModal(true);
  }, []);

  const handleContinueClick = React.useCallback(() => {
    localStorage.setItem("currentPlan", user.plan);
    localStorage.setItem("selectedPlan", selectedPlan);

    if (
      user?.subscription?.status === planStatus.CANCELED ||
      user?.subscription?.status === planStatus.INCOMPLETE_EXPIRED ||
      !user?.subscription?.status
    ) {
      doContinue();
    } else if (
      selectedPlan !== Plan.UP_TO_UNLIMITED &&
      user.clientsCountNoSample > Number(planClients(selectedPlan, true))
    ) {
      setDowngradeSelectClientsDialogOpen(true);
    } else {
      openInvoiceModal();
    }
  }, [doContinue, selectedPlan, user, openInvoiceModal]);

  const monthlyPlanList = React.useMemo(() => {
    const monthlyPlans = Object.values(Plan);

    return monthlyPlans
      .filter((plan) => {
        return ![
          Plan.PRO,
          Plan.STARTER,
          Plan.UP_TO_5,
          Plan.UP_TO_15,
          Plan.UP_TO_30,
          Plan.UP_TO_75,
          Plan.UP_TO_INFINITY,
          Plan.PROMO,
          Plan.YEARLY_UP_TO_50,
          Plan.YEARLY_UP_TO_100,
          Plan.YEARLY_UP_TO_200,
          Plan.YEARLY_UNLIMITED,
        ].includes(plan);
      })
      .map((plan) => {
        return {
          value: plan !== Plan.FREE && plan,
          disabled: checkPlanStatus(plan),
        };
      });
  }, [checkPlanStatus]);

  const yearlyPlanList = React.useMemo(() => {
    const yearlyPlans = Object.values(Plan);

    return yearlyPlans
      .filter((plan) => {
        return ![
          Plan.PRO,
          Plan.STARTER,
          Plan.UP_TO_5,
          Plan.UP_TO_15,
          Plan.UP_TO_30,
          Plan.UP_TO_75,
          Plan.UP_TO_INFINITY,
          Plan.PROMO,
          Plan.UP_TO_50,
          Plan.UP_TO_100,
          Plan.UP_TO_200,
          Plan.UP_TO_UNLIMITED,
        ].includes(plan);
      })
      .map((plan) => {
        return {
          value: plan !== Plan.FREE && plan,
          disabled: checkPlanStatus(plan),
        };
      });
  }, [checkPlanStatus]);

  const action = getPlanAction(user?.plan, selectedPlan);

  const subscriptionDaysDiff = React.useMemo(() => {
    if (
      (action !== "upgrade" && user.plan !== selectedPlan) ||
      isPlanYearly(user.plan) !== isPlanYearly(selectedPlan)
    ) {
      return null;
    }

    return user?.subscription?.status === planStatus.ACTIVE &&
      user?.plan !== Plan.UP_TO_UNLIMITED &&
      user?.plan !== Plan.YEARLY_UNLIMITED ? (
      <Typography className={s.planUpgradeInfo}>
        The plan will automatically upgrade once you exceed{" "}
        {planClients(user?.plan as Plan)} active clients. You can downgrade
        anytime.
      </Typography>
    ) : null;
  }, [action, user?.subscription?.status, user.plan, selectedPlan]);

  const monthlyPlan = monthlyBillingPlan(selectedPlan);
  const monthlyEquivalentPrice =
    billingPlanPrice[monthlyPlan]?.[tierIndex[PlanTier.DEFAULT]] ?? 0;
  const crossedOutPrice = monthlyEquivalentPrice * 12;

  const pending =
    creatingCheckoutSession ||
    createdCheckoutSession ||
    updatingSubscriptionWithAddons ||
    getHostedInvoiceUrlIn ||
    batchingArchiveClients;

  const canEditAddon = React.useCallback(
    (code: AddonCode) => {
      if (isAddonsLoading) {
        return false;
      }
      if (code === AddonCode.TEAM_ACCOUNT) {
        return !isCoachCountsLoading;
      }
      return true;
    },
    [isAddonsLoading, isCoachCountsLoading],
  );

  const originalPrice = (upsellYearlyFee / 10) * 12;
  const savings = originalPrice - upsellYearlyFee;

  const handleInvoiceClose = React.useCallback(() => {
    setShowInvoiceModal(false);
  }, []);

  const handleInvoiceConfirm = React.useCallback(() => {
    doContinue();
  }, [doContinue]);

  // Calculate summary totals
  const planPrice =
    billingPlanPrice[selectedPlan]?.[tierIndex[PlanTier.DEFAULT]] ?? 0;

  const activeAddons: Array<{
    code: string;
    quantity: number;
    isUnitBased: boolean;
    totalPrice: number;
    displayName?: string;
    description?: string;
    unitLabel?: string;
    preactivationDescription?: React.ReactNode;
  }> = useMemo(() => {
    // Only process addons that exist in the current billing interval's catalog
    // This ensures we use the correct prices for the current billing interval
    return billingIntervalAddons
      .filter((addon) => {
        const quantity = addonQuantities[addon.code] || 0;
        return quantity > 0;
      })
      .map((addon) => {
        const addonCode = addon.code;
        const quantity = addonQuantities[addonCode] || 0;
        const isUnitBased = addon.isUnitBased ?? false;
        const displayInfo = ADDON_CATALOG[addonCode];
        const totalPrice = getAddonTotalPrice(addon.price, quantity);
        return {
          code: addonCode,
          quantity,
          isUnitBased,
          totalPrice,
          ...(displayInfo || {}),
        };
      })
      .filter((addon) => addon !== null);
  }, [addonQuantities, billingIntervalAddons]);

  const DialogDescription = () => (
    <div>
      Upgrade to an annual subscription today and get 12 months for the price of
      10
      <br />
      <br />
      <strong>AND</strong>
      <br />
      <br />
      Get upgraded to a custom branded app at no extra cost for the year saving
      over $500!!
      <br />
      <br />
      <strong>
        Upgrade now for ${upsellYearlyFee} amount and save ${savings} on your
        subscription and save upto £500 for the custom branded app upgrade
      </strong>
    </div>
  );

  const isSubscriptionCanceled =
    user?.subscription?.status === planStatus.CANCELED ||
    user?.subscription?.status === planStatus.INCOMPLETE_EXPIRED;
  const isPastDueOrIncomplete =
    user?.subscription?.status === planStatus.PAST_DUE ||
    user?.subscription?.status === planStatus.INCOMPLETE ||
    user?.subscription?.status === planStatus.UNPAID;

  const isSamePlan = selectedPlan === user.plan;
  const isSwitchingBetweenMonthlyAndYearly =
    isPlanYearly(user?.plan) !== isPlanYearly(selectedPlan);

  const conditions = [
    {
      condition: isPastDueOrIncomplete && isSamePlan,
      text: "Continue",
    },
    {
      condition: isSubscriptionCanceled && isSamePlan,
      text: "Reactivate",
    },
    {
      condition: isSubscriptionCanceled && isSwitchingBetweenMonthlyAndYearly,
      text: "Reactivate & Change Plan",
    },
    {
      condition: isSubscriptionCanceled && action === "downgrade",
      text: "Reactivate & Downgrade",
    },
    {
      condition: isSubscriptionCanceled && action === "upgrade",
      text: "Reactivate & Upgrade",
    },
    { condition: isSamePlan && !hasAddonChanges, text: "Current Plan" },
    {
      condition: isSwitchingBetweenMonthlyAndYearly,
      text: "Change plan",
    },
    { condition: action === "upgrade", text: "Change plan" },
    { condition: true, text: "Change plan" },
  ];

  const buttonText =
    conditions.find((item) => item.condition)?.text || "Change plan";

  const isCurrentPlan = !action && !isSubscriptionCanceled && !hasAddonChanges;
  const isButtonDisabled =
    (isPastDueOrIncomplete && !isCurrentPlan) ||
    (!isPastDueOrIncomplete && isCurrentPlan) ||
    pending ||
    isAddonsLoading ||
    isCoachCountsLoading;

  const pendingActivationItem =
    ADDON_CATALOG[pendingAddonActivation?.addonCode];

  return (
    <Card className={clsx(s.root, className)} {...other}>
      {/* Title Section */}
      <Box>
        <Typography className={s.title}>{"Configure your plan"}</Typography>
        <Typography className={s.subtitle}>
          {Plan[user.plan] === Plan.FREE ||
          user?.subscription?.status === "trialing" ? (
            <>
              You can choose a plan without waiting for the end of the trial
              period
            </>
          ) : (
            <>
              Change the plan to the one that suits you best.
              <br />
              If you decide this plan isn't right for you, it's easy to cancel.
            </>
          )}
        </Typography>
      </Box>

      {/* Plan Selection Section */}
      <Box className={s.planSection}>
        {/* Plan Dropdown */}
        <Box>
          <Typography className={s.planLabel}>Client limit</Typography>
          <Select
            native
            variant="outlined"
            fullWidth
            value={selectedPlan}
            onChange={handlePlanChange}
            className={s.planDropdown}
            IconComponent={KeyboardArrowDownIcon}
          >
            {(isPlanYearly(user.plan) || billingCycle === "annual"
              ? yearlyPlanList
              : monthlyPlanList
            ).map(
              ({ value }) =>
                value && (
                  <option key={value} value={value}>
                    Up to {planClients(value)} clients
                  </option>
                ),
            )}
          </Select>
          <FormHelperText className={s.helperText}>
            This sets your maximum number of active clients.
          </FormHelperText>
        </Box>

        {/* Billing Cycle Selection */}
        {!isPlanYearly(user.plan) && (
          <AnimateHeightBox>
            <Typography className={s.billingCycleLabel}>
              Billing frequency
            </Typography>
            <RadioGroup
              className={s.billingCycleRadioGroup}
              value={billingCycle}
              onChange={handleBillingCycleChange}
              row
            >
              <FormControlLabel
                value="monthly"
                control={<Radio color="primary" />}
                label="Monthly"
                className={clsx(
                  s.billingCycleOption,
                  billingCycle === "monthly" && s.billingCycleOptionChecked,
                )}
              />
              <Box
                sx={{
                  position: "relative",
                  display: "inline-block",
                }}
              >
                <FormControlLabel
                  value="annual"
                  control={<Radio color="primary" />}
                  label="Annually"
                  className={clsx(
                    s.billingCycleOption,
                    billingCycle === "annual" && s.billingCycleOptionChecked,
                  )}
                />
                <Box className={s.billingCycleSavingsBadge}>2 MONTHS FREE</Box>
              </Box>
            </RadioGroup>
            {/* Description that appears when annual is selected */}
            {billingCycle === "annual" && (
              <Typography className={s.helperText}>
                <strong>Get 2 months free!</strong> When you choose annual
                billing, you pay for 10 months and get 12 months of service. You
                can change or cancel anytime.
              </Typography>
            )}
          </AnimateHeightBox>
        )}
      </Box>

      {/* Addons Section */}
      <Box className={s.addonsSection}>
        <Typography className={s.addonsTitle}>{"Add-ons"}</Typography>
        {isAddonsLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: (theme) => theme.spacing(2),
            }}
          >
            {[0, 1].map((index) => (
              <Box key={index} className={s.addonCard}>
                <Box className={s.addonInfo}>
                  <Skeleton width="50%" height={24} />
                  <Skeleton width="70%" height={18} />
                </Box>
                <Skeleton
                  variant="rectangular"
                  width={96}
                  height={32}
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            ))}
          </Box>
        ) : (
          billingIntervalAddons.map((addon) => {
            const addonCode = addon.code;
            const quantity = addonQuantities[addonCode] || 0;
            const displayInfo = ADDON_CATALOG[addonCode];
            const price = addon.price;
            const isUnitBased = addon.isUnitBased;
            const controlsDisabled = !canEditAddon(addonCode as AddonCode);

            // Skip if catalog entry doesn't exist (shouldn't happen, but handle gracefully)
            if (!displayInfo) {
              console.warn(
                `Addon catalog entry not found for code: ${addonCode}`,
              );
              return null;
            }

            const priceDisplayText = getAddonPriceDisplayText(
              price,
              isUnitBased,
              displayInfo.unitLabel,
              billingIntervalKey,
            );

            // Check if this is TEAM_ACCOUNT addon and if decreasing would go below minimum seats (active + invited)
            // TODO_ADDONS: Handle this generically for all addons
            const isTeamAccountAddon = addonCode === AddonCode.TEAM_ACCOUNT;
            const wouldGoBelowMinimumSeats =
              isTeamAccountAddon &&
              quantity > 0 &&
              quantity - 1 < minimumSeatsCount;
            const isMinusDisabled =
              quantity <= 0 ||
              wouldGoBelowMinimumSeats ||
              !canEditAddon(addonCode);

            const activeCount =
              activeCoaches?.filter(
                (coach) => coach.teamRole === TeamRole.COACH,
              ).length ?? 0;
            const invitedCount = invitedCoaches?.length ?? 0;

            const tooltipMessage = wouldGoBelowMinimumSeats ? (
              <>
                You cannot decrease the number of team seats below{" "}
                {minimumSeatsCount} ({activeCount} active + {invitedCount}{" "}
                invited). Go to{" "}
                <Link
                  href={COACH_TEAM_ACTIVE_ROUTE}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(COACH_TEAM_ACTIVE_ROUTE);
                  }}
                  sx={{ color: "inherit", textDecoration: "underline" }}
                >
                  team
                </Link>{" "}
                and archive coaches or revoke invites
              </>
            ) : undefined;

            return (
              <Box key={addonCode} className={s.addonCard}>
                <Box className={s.addonInfo}>
                  <Box className={s.addonHeader}>
                    <Typography className={s.addonName}>
                      {displayInfo.displayName}
                    </Typography>
                    <Tooltip
                      title={displayInfo.description}
                      placement="top"
                      arrow
                    >
                      <InfoOutlined className={s.addonInfoIcon} />
                    </Tooltip>
                  </Box>
                  <Typography className={s.addonDescription}>
                    {priceDisplayText}
                  </Typography>
                </Box>
                <Box className={s.addonControls}>
                  {isUnitBased ? (
                    <Box className={s.quantityControls}>
                      <Tooltip
                        title={tooltipMessage || ""}
                        placement="top"
                        arrow
                        disableHoverListener={!tooltipMessage}
                      >
                        <span>
                          <IconButton
                            className={s.quantityButton}
                            onClick={() =>
                              handleAddonQuantityChange(addonCode, -1)
                            }
                            disabled={isMinusDisabled}
                          >
                            <RemoveIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Typography className={s.quantityValue}>
                        {quantity}
                      </Typography>
                      <IconButton
                        className={s.quantityButton}
                        onClick={() => handleAddonQuantityChange(addonCode, 1)}
                        disabled={controlsDisabled}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Switch
                      className={s.addonSwitch}
                      checked={quantity > 0}
                      onChange={() => handleAddonToggle(addonCode)}
                      disabled={controlsDisabled}
                      color="primary"
                    />
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Summary Section */}
      <AnimateHeightBox>
        <Box className={s.summarySection}>
          <Typography className={s.summaryTitle}>Summary</Typography>
          <Box>
            <Box className={s.summaryItem}>
              <Box className={s.summaryItemLeft}>
                <Typography className={s.summaryItemName}>
                  Up to {planClients(selectedPlan)} clients
                </Typography>
                {subscriptionDaysDiff}
              </Box>
              <Typography className={s.summaryItemPrice} component="span">
                {formatCurrency(
                  billingCycle === "annual" ? planPrice / 12 : planPrice,
                  "usd",
                  "short",
                )}
                <Typography component="span" className={s.summaryPricePeriod}>
                  /month
                </Typography>
              </Typography>
            </Box>
            {activeAddons.map((addon) => {
              const monthlyPrice =
                billingCycle === "annual"
                  ? addon.totalPrice / 12
                  : addon.totalPrice;
              return (
                <Box key={addon.code} className={s.summaryItem}>
                  <Box className={s.summaryItemLeft}>
                    <Typography className={s.summaryItemName}>
                      {addon.displayName}
                      {addon.isUnitBased &&
                        addon.quantity > 1 &&
                        ` x ${addon.quantity} ${addon.unitLabel}s`}
                    </Typography>
                  </Box>
                  <Typography className={s.summaryItemPrice} component="span">
                    {formatCurrency(monthlyPrice, "usd", "short")}
                    <Typography
                      component="span"
                      className={s.summaryPricePeriod}
                    >
                      /month
                    </Typography>
                  </Typography>
                </Box>
              );
            })}
            {/* Subtotal */}
            <Box className={s.summarySubtotal}>
              <Box>
                <Typography className={s.summarySubtotalLabel}>
                  Subtotal
                </Typography>
              </Box>
              <Typography className={s.summarySubtotalPrice} component="span">
                {formatCurrency(
                  (billingCycle === "annual" ? planPrice / 12 : planPrice) +
                    activeAddons.reduce(
                      (sum, addon) =>
                        sum +
                        (billingCycle === "annual"
                          ? addon.totalPrice / 12
                          : addon.totalPrice),
                      0,
                    ),
                  "usd",
                  "short",
                )}
                <Typography component="span" className={s.summaryPricePeriod}>
                  /month
                </Typography>
              </Typography>
            </Box>

            {/* Hint */}

            {billingCycle === "annual" && (
              <Typography className={s.summaryHint} sx={{ textAlign: "right" }}>
                Billed annually
              </Typography>
            )}
          </Box>
        </Box>
      </AnimateHeightBox>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: (theme) => theme.spacing(1),
        }}
      >
        {/* Action Button */}
        <Button
          className={s.button}
          onClick={
            isPastDueOrIncomplete && !isCurrentPlan
              ? undefined
              : handleContinueClick
          }
          disabled={isButtonDisabled}
          fullWidth
        >
          {buttonText}
        </Button>
        {!isButtonDisabled && (
          <Typography className={s.summaryHint}>
            Click "{buttonText}" to see final pricing and proceed
          </Typography>
        )}
      </Box>

      <SelectClientsDialog
        open={downgradeSelectClientsDialogOpen}
        onClose={() => setDowngradeSelectClientsDialogOpen(false)}
        title={`Downgrade to the ${capitalize(selectedPlan)} Plan`}
        onConfirm={(clients) => {
          batchArchiveClients(
            {
              clients: clients,
              archived: true,
            },
            {
              onSuccess: () => {
                openInvoiceModal();
              },
            },
          );
        }}
        minSelected={
          user.clientsCountNoSample -
          (planClients(selectedPlan, true) as number)
        }
        user={user}
        selectedPlan={selectedPlan}
      />

      <ConfirmActionDialog
        title={`STRIDIST BLACK FRIDAY DEAL!!`}
        description={<DialogDescription />}
        onCancel={() => cancelYearlyUpsell()}
        onConfirm={handleContinueClick}
        confirmButtonText="Upgrade to Annual"
        open={showUpsellYearlyPlan}
        classNameTitle={s.titleModal}
      />

      <InvoiceBreakdownDialog
        open={showInvoiceModal}
        plan={getSubscriptionUpdatePayload().plan}
        addons={getSubscriptionUpdatePayload().addons}
        loading={updatingSubscriptionWithAddons}
        onClose={handleInvoiceClose}
        onConfirm={handleInvoiceConfirm}
      />

      <SubscriptionUpdatePolling
        open={openPolling}
        onClose={handleClosePolling}
        expectedPlan={selectedPlan.toString()}
      />

      {/* Pre-activation Dialog */}
      {pendingAddonActivation &&
        (pendingAddonActivation.addonCode === AddonCode.CBA ? (
          <CBAPreactivationDialog
            title={
              pendingActivationItem?.preactivationTitle ||
              `Activate ${pendingActivationItem?.displayName || "Addon"}?`
            }
            onCancel={handlePreactivationCancel}
            onConfirm={handlePreactivationConfirm}
            confirmButtonText="Activate"
            open={preactivationDialogOpen}
          />
        ) : (
          <ConfirmActionDialog
            title={
              pendingActivationItem?.preactivationTitle ||
              `Activate ${pendingActivationItem?.displayName || "Addon"}?`
            }
            description={
              <Box className={s.preactivationDescription}>
                {pendingActivationItem?.preactivationDescription ||
                  "Are you sure you want to activate this addon?"}
              </Box>
            }
            onCancel={handlePreactivationCancel}
            onConfirm={handlePreactivationConfirm}
            confirmButtonText="Activate"
            open={preactivationDialogOpen}
            classNameTitle={s.titleModal}
            className={s.preactivationDialog}
          />
        ))}
    </Card>
  );
}
